import React from 'react';
import PropTypes from 'prop-types';
import { useWateringSummary } from '../../utils/reactQueryHooks';
import { useNow } from '../../hooks/useNow';
import { formatAge, ageMsFromIso } from '../../utils/timeUtils';
import '../../styles/wateringStatus.css';

/**
 * "Watering now" / "Last watered" indicator for an irrigation action, driven by the R3
 * watering-events backend (see WATERING_EVENTS_BACKEND_SPEC_README.md).
 *
 * Graceful by design: when the backend endpoint is not configured or has no data yet, the
 * summary is null and this renders nothing (tile) or a muted placeholder (detail). It never
 * throws, so it is safe to ship before the Pi backend exists.
 */
export default function WateringStatus({ actionMac, variant = 'tile' }) {
    const now = useNow(30000);
    const { data: summary } = useWateringSummary(actionMac);

    if (!summary) {
        // Feature off or no data yet. Detail view shows a quiet placeholder; tile shows nothing.
        return variant === 'detail'
            ? <p className="zone-updated watering-status-empty">Last watered: —</p>
            : null;
    }

    const isRunning = summary.wateringState === 'running';
    const ageMs = ageMsFromIso(summary.lastWateredAt, now);
    const lastLabel = formatAge(ageMs);
    const wasInterrupted = summary.lastStatus === 'interrupted';

    if (variant === 'detail') {
        return (
            <p className="zone-updated watering-status watering-status-detail">
                {isRunning ? (
                    <span className="watering-badge watering-badge-running">
                        <span className="watering-dot" aria-hidden="true"></span>
                        Watering now
                    </span>
                ) : lastLabel ? (
                    <>
                        <span className="watering-status-label">Last watered:</span>{' '}
                        <span className="watering-status-value">{lastLabel}</span>
                        {summary.lastDurationSeconds != null && (
                            <span className="watering-status-muted"> · {summary.lastDurationSeconds}s run</span>
                        )}
                        {wasInterrupted && (
                            <span className="watering-badge watering-badge-interrupted"> Interrupted</span>
                        )}
                    </>
                ) : (
                    <span className="watering-status-label">Last watered: Never</span>
                )}
            </p>
        );
    }

    // Compact tile variant.
    if (isRunning) {
        return (
            <div className="watering-badge watering-badge-running watering-badge-tile">
                <span className="watering-dot" aria-hidden="true"></span>
                Watering now
            </div>
        );
    }
    if (lastLabel) {
        return (
            <div className={"watering-tile-line" + (wasInterrupted ? " watering-tile-interrupted" : "")}>
                {wasInterrupted ? 'Interrupted' : `Watered ${lastLabel}`}
            </div>
        );
    }
    return null;
}

WateringStatus.propTypes = {
    actionMac: PropTypes.string,
    variant: PropTypes.oneOf(['tile', 'detail']),
};
